import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { navLinks } from '../data';
import { AnimatePresence, motion } from 'framer-motion';
import { SerbiaFlagIcon, UKFlagIcon } from './icons/LanguageIcons.jsx';

const Navbar = React.forwardRef(() => {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState('hero');
  const [toggle, setToggle] = useState(false);
  const navbarInnerRef = useRef(null);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang).then(() => {
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('lang', lang);
      window.history.pushState({}, '', currentUrl);
    });
    setLangDropdownOpen(false);
    setToggle(false);
  };

  const handleSmoothScroll = (targetId) => {
    setToggle(false);
    const elementToScrollTo = document.getElementById(targetId);
    if (elementToScrollTo) {
      const offsetTop = elementToScrollTo.offsetTop - 30;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = navLinks
        .map((link) => document.getElementById(link.id))
        .filter(Boolean);
      const scrollPosition = window.scrollY + 30;

      let currentSectionId = 'hero';
      for (const section of sections) {
        if (section.offsetTop <= scrollPosition) {
          currentSectionId = section.id;
        }
      }
      setActive(currentSectionId);
    };

    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const offsetTop = element.offsetTop - 30;
          window.scrollTo({ top: offsetTop, behavior: 'auto' });
        }
      }, 150);
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      ref={navbarInnerRef}
      className={`w-full flex items-center py-5 fixed top-0 z-40 ${active === 'hero' ? 'bg-transparent' : 'bg-primary'} transition-colors duration-300`}
    >
      <div className="w-full flex justify-between items-center max-w-7xl mx-auto md:px-16 px-6">
        <a
          href="#hero"
          className="text-white text-[26px] lg:text-[36px] font-bold cursor-pointer flex items-start"
          onClick={() => handleSmoothScroll('hero')}
        >
          Luka Vukanović
        </a>
        <ul className="list-none hidden md:flex flex-row items-center gap-10">
          {navLinks.map(
            (nav) =>
              nav.id !== 'hero' && (
                <li
                  key={nav.id}
                  className={`${active === nav.id ? 'text-white' : 'text-slate-400'} hover:text-white text-[18px] font-bold cursor-pointer`}
                >
                  <a
                    href={`#${nav.id}`}
                    onClick={() => handleSmoothScroll(nav.id)}
                  >
                    {t(`nav.${nav.id}`)}
                  </a>
                </li>
              )
          )}
          <li className="relative" ref={dropdownRef}>
            <button
              onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              className="flex items-center"
              aria-label={t('nav.change_language')}
            >
              <div className="w-8 h-8 rounded-md overflow-hidden">
                {i18n.language.startsWith('sr') ? (
                  <SerbiaFlagIcon className={'w-[90%] h-[70%]'} />
                ) : (
                  <UKFlagIcon className={'w-[90%] h-[70%]'} />
                )}
              </div>
            </button>
            <AnimatePresence>
              {langDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-32 bg-tertiary rounded-lg shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-secondary hover:bg-primary"
                  >
                    <UKFlagIcon className="w-6 flex-shrink-0" />
                    <span>English</span>
                  </button>
                  <button
                    onClick={() => handleLanguageChange('sr')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-secondary hover:bg-primary"
                  >
                    <SerbiaFlagIcon className="w-6 flex-shrink-0" />
                    <span>Srpski</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        </ul>
        <div className="md:hidden flex flex-1 justify-end items-center">
          <img
            src={`${import.meta.env.BASE_URL}/assets/${toggle ? 'close.svg' : 'menu.svg'}`}
            alt="menu"
            className="w-[28px] h-[28px] object-contain cursor-pointer"
            onClick={() => setToggle(!toggle)}
          />
          <div
            className={`${!toggle ? 'hidden' : 'flex'} p-6 bg-gradient-to-b from-gray-900 to-primary absolute top-20 right-0 mx-4 my-2 min-w-[140px] z-30 rounded-xl shadow-lg`}
          >
            <ul className="list-none flex justify-end items-start flex-1 flex-col gap-4">
              {navLinks.map(
                (nav) =>
                  nav.id !== 'hero' && (
                    <li
                      key={nav.id}
                      className={`font-medium cursor-pointer text-[16px] ${active === nav.id ? 'text-quaternary' : 'text-secondary'}`}
                      onClick={() => handleSmoothScroll(nav.id)}
                    >
                      <a href={`#${nav.id}`}>{t(`nav.${nav.id}`)}</a>
                    </li>
                  )
              )}
              <li className="flex flex-col items-start gap-2 mt-4 border-t border-slate-700 w-full pt-4">
                <span className="text-secondary font-medium">
                  {t('nav.language_label')}:
                </span>
                <div className="flex gap-4">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`w-12 h-8 rounded-sm overflow-hidden transition-all pb-[2.8px] ${i18n.language.startsWith('en') ? 'bg-quaternary' : 'bg-transparent'}`}
                  >
                    <UKFlagIcon className={'w-[100%] h-[80%]'} />
                  </button>
                  <button
                    onClick={() => handleLanguageChange('sr')}
                    className={`w-12 h-8 rounded-sm overflow-hidden transition-all pb-[2.8px] ${i18n.language.startsWith('sr') ? 'bg-quaternary' : 'bg-transparent'}`}
                  >
                    <SerbiaFlagIcon className={'w-[100%] h-[80%]'} />
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
});

export default Navbar;
